import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FloppyDisk, Plus, Printer, Trash, UploadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { parseProductsSupplied } from "@/lib/supplierProducts";
import { publicAssetUrl } from "@/lib/api";

function toNumber(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/** Blank weight → 1 so lines are editable before weight is entered; explicit 0 stays 0. */
function lineWeightFactor(weightStr, weightN) {
  if (weightStr === "" || weightStr == null) return 1;
  return weightN;
}

function roundRupee(n) {
  return Math.round(n);
}

function formatRupee(n) {
  return roundRupee(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function toDateInputValue(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** ISO yyyy-mm-dd (date input / saved payload) → dd-mm-yyyy for quotation header & print */
function formatQuoteDateDisplay(isoValue) {
  if (!isoValue) return "";
  const trimmed = String(isoValue).trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return trimmed;
  const day = String(d.getDate()).padStart(2, "0");
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const y = d.getFullYear();
  return `${day}-${m}-${y}`;
}

function initialLine(name = "") {
  return { description: name, qty: "1", ratePerDay: "", weight: "" };
}

const DEFAULT_TERMS = [
  "Above Rates Valid For 0 Days Only.",
  "GST Shall be Payable 18% Extra As Applicable.",
  "Material will dispatched from Bangalore and transportation charges of material shall be in scope of the client for both side.",
  "Once the PO, advance payment received, material will start delivered by 8th day and as per customer schedule.",
  "The material shall at all times remain sole property of supplier and reserves all rights to take the material back in case of dispute or non payment of bills for more than 45 days past the due date.",
  "Security Cheque with outdated against the total value of material.",
  "PDCs against monthly rental of each month also as security.",
  "Payment against bill shall be made on 7th of every month.",
  "Clients shall be liable for any kind damage and loss of material.",
  "The client's executive shall be present at dispatch and return of material. In case of dispute final quantity decision remains with supplier.",
  "While returning the material client shall be responsible to arrange all legal documents for proper transit of goods.",
  "The above rates do not include any kind of installation or erection of any structure.",
  "No discounts of any sorts shall be asked for.",
  "Lost material shall be charged at 45/- per kg.",
  "Depreciation material / broken material will be charged at 45/- per kg.",
  "All disputes shall be subject to local jurisdiction.",
  "Material shifting to and fro should be informed prior to three days.",
  "Various damage charges will be imposed to client for broken spares/accessories.",
];

const SIGNATURE_MAX_BYTES = 300 * 1024; // 300 KB
const SIGNATURE_MIN_WIDTH = 240;
const SIGNATURE_MIN_HEIGHT = 60;
const SIGNATURE_MIN_ASPECT = 2.0;
const SIGNATURE_MAX_ASPECT = 6.0;

function buildQuotationPayload(state) {
  return {
    supplierName: state.supplierName,
    supplierAddress: state.supplierAddress,
    supplierGstin: state.supplierGstin,
    customerName: state.customerName,
    customerAddress: state.customerAddress,
    siteLocation: state.siteLocation,
    quoteNo: state.quoteNo,
    quoteDate: state.quoteDate,
    gstPercent: state.gstPercent,
    securityAmount: state.securityAmount,
    lines: state.lines,
    termsText: state.termsText,
    signatureUrl: state.signatureUrl,
    watermarkUrl: state.watermarkUrl,
  };
}

export default function SupplierQuotationBuilder() {
  const { assignmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const quotationIdParam = (searchParams.get("q") || "").trim();
  const assignmentFromState = location.state?.assignment || null;
  const [assignment, setAssignment] = useState(assignmentFromState);
  const [loading, setLoading] = useState(true);
  const [bootstrapReady, setBootstrapReady] = useState(false);
  const [savedQuotationId, setSavedQuotationId] = useState(null);
  const [savingQuotation, setSavingQuotation] = useState(false);
  const [watermarkUrl, setWatermarkUrl] = useState("");
  const [signatureUrl, setSignatureUrl] = useState("");
  const [supplierDefaults, setSupplierDefaults] = useState([]);
  const [productNameToId, setProductNameToId] = useState({});

  const [supplierName, setSupplierName] = useState("");
  const [supplierAddress, setSupplierAddress] = useState("");
  const [supplierGstin, setSupplierGstin] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [siteLocation, setSiteLocation] = useState("");
  const [quoteNo, setQuoteNo] = useState("1");
  const [quoteDate, setQuoteDate] = useState(toDateInputValue(new Date().toISOString()));
  const [gstPercent, setGstPercent] = useState("18");
  const [securityAmount, setSecurityAmount] = useState("");
  const [lines, setLines] = useState([initialLine("")]);
  const [termsText, setTermsText] = useState(DEFAULT_TERMS.map((t, i) => `${i + 1}. ${t}`).join("\n"));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const calls = [
          assignmentFromState
            ? Promise.resolve({ data: assignmentFromState })
            : api.get(`/supplier/assignments/${assignmentId}`),
          api.get("/supplier/profile"),
          api.get("/products?limit=500"),
        ];
        const [assignRes, profileRes, productsRes] = await Promise.all(calls);
        if (cancelled) return;
        setAssignment(assignRes.data);
        const p = profileRes.data || {};
        setSupplierName(p.quotation_supplier_name || p.name || "");
        setSupplierAddress(p.quotation_supplier_address || p.company_name || "");
        setSupplierGstin(p.quotation_supplier_gstin || p.gst_number || "");
        setSecurityAmount(
          p.quotation_security_amount != null ? String(p.quotation_security_amount) : ""
        );
        if (p.quotation_terms) setTermsText(String(p.quotation_terms));
        setWatermarkUrl(p.quotation_watermark_url ? publicAssetUrl(p.quotation_watermark_url) : "");
        setSupplierDefaults(parseProductsSupplied(p.products_supplied));
        const items = productsRes?.data?.items || productsRes?.data || [];
        const map = {};
        for (const it of items) {
          const k = String(it?.name || "").trim().toLowerCase();
          const id = String(it?.id || "").trim();
          if (k && id) map[k] = id;
        }
        setProductNameToId(map);
      } catch {
        if (!cancelled) {
          toast.error("Lead assignment not found");
          navigate("/supplier");
        }
      } finally {
        if (!cancelled) setBootstrapReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignmentFromState, assignmentId, navigate]);

  const applyPayload = useCallback((pl) => {
    if (!pl || typeof pl !== "object") return;
    if (pl.supplierName != null) setSupplierName(String(pl.supplierName));
    if (pl.supplierAddress != null) setSupplierAddress(String(pl.supplierAddress));
    if (pl.supplierGstin != null) setSupplierGstin(String(pl.supplierGstin));
    if (pl.customerName != null) setCustomerName(String(pl.customerName));
    if (pl.customerAddress != null) setCustomerAddress(String(pl.customerAddress));
    if (pl.siteLocation != null) setSiteLocation(String(pl.siteLocation));
    if (pl.quoteNo != null) setQuoteNo(String(pl.quoteNo));
    if (pl.quoteDate != null) setQuoteDate(toDateInputValue(pl.quoteDate));
    if (pl.gstPercent != null) setGstPercent(String(pl.gstPercent));
    if (pl.securityAmount != null) setSecurityAmount(String(pl.securityAmount));
    if (Array.isArray(pl.lines)) {
      if (pl.lines.length) {
        setLines(
          pl.lines.map((l) => ({
            description: l.description != null ? String(l.description) : "",
            qty: l.qty != null ? String(l.qty) : "1",
            ratePerDay: l.ratePerDay != null ? String(l.ratePerDay) : "",
            weight: l.weight != null ? String(l.weight) : "",
          }))
        );
      } else {
        setLines([initialLine("")]);
      }
    }
    if (pl.termsText != null) setTermsText(String(pl.termsText));
    if (pl.signatureUrl != null) setSignatureUrl(String(pl.signatureUrl));
    if (pl.watermarkUrl != null) setWatermarkUrl(String(pl.watermarkUrl));
  }, []);

  useEffect(() => {
    if (!bootstrapReady) return;
    if (!assignment?.lead) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      if (quotationIdParam) {
        try {
          const r = await api.get(`/supplier/quotations/${quotationIdParam}`);
          if (cancelled) return;
          const doc = r.data;
          applyPayload(doc.payload || {});
          if (doc.quote_no != null) setQuoteNo(String(doc.quote_no));
          setSavedQuotationId(doc.id);
        } catch {
          if (!cancelled) {
            toast.error("Could not load saved quotation");
            setSearchParams({}, { replace: true });
            setSavedQuotationId(null);
            setLoading(false);
          }
        }
        if (!cancelled) setLoading(false);
        return;
      }

      setSavedQuotationId(null);
      try {
        const nr = await api.get("/supplier/quotations/next-number");
        if (!cancelled) setQuoteNo(String(nr.data?.next ?? 1));
      } catch {
        if (!cancelled) setQuoteNo("1");
      }

      const lead = assignment.lead;
      setCustomerName(lead.buyer_company || lead.buyer_name || "");
      setSiteLocation(lead.city || "");
      const byId = new Map(supplierDefaults.map((x) => [x.product_id, x]));
      setLines(
        (lead.items || []).length
          ? lead.items.map((item) => {
              const resolvedId =
                item.product_id ||
                productNameToId[String(item.product_name || "").trim().toLowerCase()] ||
                "";
              const d = (resolvedId && byId.get(resolvedId)) || null;
              return {
                description: item.product_name || "",
                qty: "1",
                ratePerDay: d?.rate_per_day != null ? String(d.rate_per_day) : "",
                weight: d?.weight != null ? String(d.weight) : "",
              };
            })
          : [initialLine("")]
      );
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    bootstrapReady,
    assignment,
    supplierDefaults,
    productNameToId,
    quotationIdParam,
    applyPayload,
    setSearchParams,
  ]);

  const saveQuotation = async () => {
    if (!assignment?.id) {
      toast.error("Missing assignment");
      return;
    }
    const payload = buildQuotationPayload({
      supplierName,
      supplierAddress,
      supplierGstin,
      customerName,
      customerAddress,
      siteLocation,
      quoteNo,
      quoteDate,
      gstPercent,
      securityAmount,
      lines,
      termsText,
      signatureUrl,
      watermarkUrl,
    });
    const qn = String(quoteNo || "").trim() || "1";
    setSavingQuotation(true);
    try {
      if (savedQuotationId || quotationIdParam) {
        const id = savedQuotationId || quotationIdParam;
        await api.put(`/supplier/quotations/${id}`, { quote_no: qn, payload });
        setSavedQuotationId(id);
        setSearchParams({ q: id }, { replace: true });
        toast.success("Quotation saved");
      } else {
        const r = await api.post("/supplier/quotations", {
          assignment_id: assignment.id,
          quote_no: qn,
          payload,
        });
        const newId = r.data?.id;
        if (newId) {
          setSavedQuotationId(newId);
          setSearchParams({ q: newId }, { replace: true });
        }
        toast.success("Quotation saved");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not save quotation");
    }
    setSavingQuotation(false);
  };

  const isBuyRequirement = (assignment?.lead?.requirement_type || "").toLowerCase() === "buy";

  const lineComputed = useMemo(
    () =>
      lines.map((l) => {
        const qty = toNumber(l.qty);
        const rate = toNumber(l.ratePerDay);
        const weight = toNumber(l.weight);
        if (isBuyRequirement) {
          const weightFactor = lineWeightFactor(l.weight, weight);
          const lineBase = qty * rate * weightFactor;
          return {
            ...l,
            weightN: weight,
            amountPerDay: roundRupee(lineBase),
            amountPerMonth: roundRupee(lineBase * 30),
          };
        }
        return {
          ...l,
          weightN: weight,
          amountPerDay: qty * rate,
          amountPerMonth: qty * rate * 30,
        };
      }),
    [lines, isBuyRequirement]
  );

  const totals = useMemo(() => {
    const totalPerDay = lineComputed.reduce((sum, l) => sum + l.amountPerDay, 0);
    const subTotal = lineComputed.reduce(
      (sum, l) => sum + (isBuyRequirement ? l.amountPerDay : l.amountPerMonth),
      0
    );
    const totalQtyWeight = lineComputed.reduce((sum, l) => {
      const qty = toNumber(l.qty);
      const weightFactor = lineWeightFactor(l.weight, l.weightN);
      return sum + qty * weightFactor;
    }, 0);
    const gstPct = toNumber(gstPercent);
    const gst = isBuyRequirement
      ? roundRupee((subTotal * gstPct) / 100)
      : (subTotal * gstPct) / 100;
    const grandTotal = subTotal + gst;
    const securityRate = isBuyRequirement ? 0 : toNumber(securityAmount);
    const security = isBuyRequirement ? 0 : securityRate * totalQtyWeight;
    return { totalPerDay, subTotal, gst, grandTotal, security, payable: grandTotal + security };
  }, [lineComputed, gstPercent, securityAmount, isBuyRequirement]);

  const formatMoney = (n) =>
    isBuyRequirement ? formatRupee(n) : Number(n).toLocaleString("en-IN");

  const onWatermarkFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setWatermarkUrl(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const onSignatureFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Upload a valid image file for signature");
      return;
    }
    if (file.size > SIGNATURE_MAX_BYTES) {
      toast.error("Signature image must be 300 KB or smaller");
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || 0;
      const h = img.naturalHeight || 0;
      URL.revokeObjectURL(objectUrl);
      if (w < SIGNATURE_MIN_WIDTH || h < SIGNATURE_MIN_HEIGHT) {
        toast.error("Signature image is too small (min 240x60)");
        return;
      }
      const ratio = w / h;
      if (ratio < SIGNATURE_MIN_ASPECT || ratio > SIGNATURE_MAX_ASPECT) {
        toast.error("Use a wide signature image (aspect ratio between 2:1 and 6:1)");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setSignatureUrl(String(reader.result || ""));
      reader.readAsDataURL(file);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      toast.error("Could not read signature image");
    };
    img.src = objectUrl;
  };

  const updateLine = (idx, key, value) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [key]: value } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, initialLine("")]);
  const removeLine = (idx) => setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const printQuotation = () => {
    const previousTitle = document.title;
    document.title = "";
    let restored = false;
    const restoreTitle = () => {
      if (restored) return;
      restored = true;
      document.title = previousTitle;
    };
    window.addEventListener("afterprint", restoreTitle, { once: true });
    window.setTimeout(restoreTitle, 5000);
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500">
        Loading quotation builder...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 p-4 sm:p-6 print:bg-white print:p-0">
      <style>
        {`@page {
          size: A4;
          margin: 0;
        }
        @media print {
          html,
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .quotation-print-root {
            box-sizing: border-box;
            width: 210mm;
            max-width: 210mm;
            margin: 0 auto;
            padding: 12mm 10mm;
            overflow: visible;
          }
          .quotation-print-sheet {
            max-height: none !important;
            overflow: visible !important;
          }
          .quotation-print-sheet > div {
            transform: none !important;
            min-width: 0 !important;
          }
        }`}
      </style>
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-4 items-start print:block">
        <Card className="print:hidden lg:sticky lg:top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto rounded-none shadow-none border border-neutral-300">
          <CardHeader className="pb-3 border-b border-neutral-200 bg-neutral-50">
            <CardTitle className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold tracking-wide uppercase text-neutral-700">Quotation Builder</span>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button variant="outline" onClick={() => navigate("/supplier")}>
                  <ArrowLeft size={16} className="mr-1" /> Back
                </Button>
                <Button variant="default" onClick={saveQuotation} disabled={savingQuotation}>
                  <FloppyDisk size={16} className="mr-1" />
                  {savingQuotation ? "Saving…" : savedQuotationId || quotationIdParam ? "Save changes" : "Save quotation"}
                </Button>
                <Button onClick={printQuotation}>
                  <Printer size={16} className="mr-1" /> Print / Save PDF
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Supplier Name</Label><Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Quotation No</Label><Input value={quoteNo} onChange={(e) => setQuoteNo(e.target.value)} /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Supplier Address</Label><Input value={supplierAddress} onChange={(e) => setSupplierAddress(e.target.value)} /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Date</Label><Input type="date" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)} /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Supplier GSTIN</Label><Input value={supplierGstin} onChange={(e) => setSupplierGstin(e.target.value)} /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Site Location</Label><Input value={siteLocation} onChange={(e) => setSiteLocation(e.target.value)} /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Customer Name</Label><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Customer Address</Label><Input value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">GST %</Label><Input value={gstPercent} onChange={(e) => setGstPercent(e.target.value)} /></div>
            {!isBuyRequirement && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Security Rate (per Qty × Weight)</Label>
                <Input value={securityAmount} onChange={(e) => setSecurityAmount(e.target.value)} placeholder="Optional rate multiplier" />
              </div>
            )}
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Watermark Image</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="file" accept="image/*" onChange={onWatermarkFileChange} className="max-w-sm" />
                <Button type="button" variant="outline" onClick={() => setWatermarkUrl("")}>
                  <UploadSimple size={14} className="mr-1" /> Clear watermark
                </Button>
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Authorised Signature</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={onSignatureFileChange} className="max-w-sm" />
                <Button type="button" variant="outline" onClick={() => setSignatureUrl("")}>
                  <UploadSimple size={14} className="mr-1" /> Clear signature
                </Button>
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">
                Use PNG/JPG/WebP, max 300 KB, minimum 240x60, and wide ratio (2:1 to 6:1).
              </p>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">Terms &amp; Conditions</Label>
              <textarea
                value={termsText}
                onChange={(e) => setTermsText(e.target.value)}
                className="mt-1 min-h-[180px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Add one term per line"
              />
            </div>
          </CardContent>
        </Card>

        <div className="quotation-print-sheet bg-white border border-neutral-200 relative overflow-hidden print:border-0 lg:max-h-[calc(100vh-2rem)] lg:overflow-auto lg:[&>div]:origin-top lg:[&>div]:scale-[0.8]">
          {watermarkUrl ? (
            <img
              src={watermarkUrl}
              alt=""
              className="pointer-events-none absolute inset-0 m-auto opacity-10 max-w-[70%] max-h-[70%] object-contain"
            />
          ) : null}
          <div className="quotation-print-root relative py-6 px-0 space-y-4 lg:min-w-[600px] print:py-0">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-2xl font-bold">{supplierName || "Supplier Name"}</p>
                <p className="text-xs text-neutral-600 whitespace-pre-line">{supplierAddress || "Supplier Address"}</p>
                <p className="text-xs font-semibold mt-1">GSTIN: {supplierGstin || "—"}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">QUOTATION</p>
                <p className="text-sm">No: {quoteNo || "—"}</p>
                <p className="text-sm">Date: {formatQuoteDateDisplay(quoteDate) || "—"}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-semibold">To:</p>
                <p className="font-bold">{customerName || "Customer"}</p>
                <p className="text-xs whitespace-pre-line">{customerAddress || "Address"}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold">Site Location:</p>
                <p>{siteLocation || "—"}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border border-neutral-300 text-sm">
                <thead>
                  <tr className="bg-blue-900 text-white">
                    <th className="border border-neutral-300 p-2 w-12">#</th>
                    <th className="border border-neutral-300 p-2 text-left w-[320px]">Description</th>
                    <th className="border border-neutral-300 p-2 w-24">Qty</th>
                    <th className="border border-neutral-300 p-2 w-32">{isBuyRequirement ? "Rate/kg" : "Rate/Day"}</th>
                    <th className="border border-neutral-300 p-2 w-24">Weight</th>
                    <th className="border border-neutral-300 p-2 w-48">{isBuyRequirement ? "Total Value" : "Total/Month(30 Days)"}</th>
                    <th className="border border-neutral-300 p-2 w-20 print:hidden">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lineComputed.map((line, idx) => (
                    <tr key={idx}>
                      <td className="border border-neutral-300 p-2 text-center">{idx + 1}</td>
                      <td className="border border-neutral-300 p-1 min-w-[320px]">
                        <div className="print:hidden">
                          <Input
                            value={line.description}
                            onChange={(e) => updateLine(idx, "description", e.target.value)}
                            className="h-8 border-0 p-1 bg-yellow-100/70 focus-visible:ring-yellow-400"
                          />
                        </div>
                        <p className="hidden print:block text-sm">{line.description || "—"}</p>
                      </td>
                      <td className="border border-neutral-300 p-1 text-center">
                        <div className="print:hidden">
                          <Input value={line.qty} onChange={(e) => updateLine(idx, "qty", e.target.value)} className="h-8 border-0 p-1 text-center bg-yellow-100/70 focus-visible:ring-yellow-400" />
                        </div>
                        <p className="hidden print:block text-sm text-center">{line.qty || "0"}</p>
                      </td>
                      <td className="border border-neutral-300 p-1 text-center">
                        <div className="print:hidden">
                          <Input value={line.ratePerDay} onChange={(e) => updateLine(idx, "ratePerDay", e.target.value)} className="h-8 border-0 p-1 text-center bg-yellow-100/70 focus-visible:ring-yellow-400" />
                        </div>
                        <p className="hidden print:block text-sm text-center">{line.ratePerDay || "0"}</p>
                      </td>
                      <td className="border border-neutral-300 p-1 text-center">
                        <div className="print:hidden">
                          <Input value={line.weight} onChange={(e) => updateLine(idx, "weight", e.target.value)} className="h-8 border-0 p-1 text-center bg-yellow-100/70 focus-visible:ring-yellow-400" />
                        </div>
                        <p className="hidden print:block text-sm text-center">{line.weight || "—"}</p>
                      </td>
                      <td className="border border-neutral-300 p-2 text-right">
                        ₹ {formatMoney(isBuyRequirement ? line.amountPerDay : line.amountPerMonth)}
                      </td>
                      <td className="border border-neutral-300 p-1 text-center print:hidden">
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(idx)}>
                          <Trash size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  <tr className="print:hidden">
                    <td colSpan={7} className="border border-neutral-300 p-2">
                      <Button type="button" variant="outline" onClick={addLine} className="bg-yellow-300/90 hover:bg-yellow-300 border-yellow-400 text-neutral-900">
                        <Plus size={14} className="mr-1" /> Add Product Line
                      </Button>
                    </td>
                  </tr>
                  {!isBuyRequirement && (
                    <tr>
                      <td colSpan={4} className="border border-neutral-300 p-2 text-right font-semibold">Total Value/Day</td>
                      <td className="border border-neutral-300 p-2" />
                      <td className="border border-neutral-300 p-2 text-right font-semibold">₹ {formatMoney(totals.totalPerDay)}</td>
                      <td className="border border-neutral-300 p-2 print:hidden" />
                    </tr>
                  )}
                  <tr>
                    <td colSpan={4} className="border border-neutral-300 p-2 text-right font-semibold">GST @{gstPercent || 0}%</td>
                    <td className="border border-neutral-300 p-2" />
                    <td className="border border-neutral-300 p-2 text-right font-semibold">₹ {formatMoney(totals.gst)}</td>
                    <td className="border border-neutral-300 p-2 print:hidden" />
                  </tr>
                  <tr>
                    <td colSpan={4} className="border border-neutral-300 p-2 text-right font-semibold">Total Value (After GST)</td>
                    <td className="border border-neutral-300 p-2" />
                    <td className="border border-neutral-300 p-2 text-right font-semibold">₹ {formatMoney(totals.grandTotal)}</td>
                    <td className="border border-neutral-300 p-2 print:hidden" />
                  </tr>
                  {!isBuyRequirement && (
                    <tr>
                      <td colSpan={4} className="border border-neutral-300 p-2 text-right font-semibold">Security Amount (Qty × Weight)</td>
                      <td className="border border-neutral-300 p-2" />
                      <td className="border border-neutral-300 p-2 text-right font-semibold">₹ {formatMoney(totals.security)}</td>
                      <td className="border border-neutral-300 p-2 print:hidden" />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pt-3">
              <p className="font-bold text-xl mb-2">Terms &amp; Conditions:</p>
              <div className="text-[10px] leading-4 whitespace-pre-line">
                {termsText || "No terms added."}
              </div>
            </div>

            <div className="pt-8 flex justify-end">
              <div className="text-right min-w-[220px]">
                <p className="font-bold text-lg">{supplierName || "Supplier"}</p>
                {signatureUrl ? (
                  <div className="mt-3 flex justify-end">
                    <img
                      src={signatureUrl}
                      alt="Authorised signature"
                      className="h-14 max-w-[220px] object-contain"
                    />
                  </div>
                ) : null}
                <div className="mt-8 border-t border-neutral-700 pt-2 text-xs uppercase tracking-wide">
                  Authorised Signatory
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
